// frontend/src/storyStorage.js
// Preserves synopsis + metadata for matching stories, but NEVER resurrects locally cached stories deleted from the server.
// FIXED: Remote story list is authoritative whenever the server returns a valid list.
// FIXED: Local cache is only a fallback when the server cannot be reached.
// FIXED: normalizeStory keeps About Author + Images + Description + Cover.
// FIXED: normalizeStory preserves storyType.
// FIXED: mergeStories signature remains stable: mergeStories(remote, stored).
// FIXED: preserve visibility/publication metadata so Story Hub and Storylines survive refresh.
// Build: 2026-06-16-story-visibility-publication-preserve-v1

const STORAGE_KEY = "ns_stories_v1";

const LEGACY_STORY_KEYS = [
  "ns_storylines",
  "ns_story_hub_stories",
  "ns_story_forum_10",
  "ns_story_forum_11",
];

function safeParseJSON(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function cleanText(v) {
  return String(v || "").trim();
}

function canWriteLocal() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("ns.auth") === "1";
  } catch {
    return false;
  }
}

function isObject(v) {
  return v && typeof v === "object" && !Array.isArray(v);
}

/* =========================
   CANON NORMALIZATION
   ========================= */

export function isCanonApproved(scene) {
  if (!scene || typeof scene !== "object") return false;

  const type = String(scene.type || scene.kind || "").toLowerCase();
  const status = String(scene.status || scene.state || "").toLowerCase();

  if (type === "canon") return true;
  if (status === "approved") return true;
  if (status === "canon") return true;

  return false;
}

function normalizeStory(raw) {
  if (!raw || typeof raw !== "object") return null;

  const id =
    raw._id ||
    raw.id ||
    raw.localId ||
    raw.storyId ||
    raw.story_id ||
    `legacy_${Date.now()}_${Math.random().toString(36).slice(2)}`;

  const title = raw.title || raw.name || raw.storyTitle || "Untitled Storyline";

  const forum =
    raw.forum ||
    raw.genre ||
    raw.forumName ||
    raw.category ||
    raw.preferredForum ||
    "Unclassified Forum";

  const writingMode =
    cleanText(raw.writingMode || raw.mode || raw.storyMode) || "Solo";

  const storyTypeRaw = cleanText(raw.storyType || raw.story_type || raw.storyKind || "");
  const storyType = storyTypeRaw.toLowerCase() === "short" ? "short" : "longform";

  const authorProfile =
    (isObject(raw.authorProfile) && raw.authorProfile) ||
    (isObject(raw.author) && raw.author) ||
    null;

  const authorName = cleanText(authorProfile?.name) || cleanText(raw.authorName) || "";

  const authorUsername =
    cleanText(authorProfile?.username) ||
    cleanText(raw.authorUsername) ||
    (typeof raw.author === "string" ? cleanText(raw.author) : "") ||
    cleanText(raw.createdBy) ||
    "";

  const authorId = cleanText(authorProfile?.id) || cleanText(raw.authorId) || "";

  const authorAvatarUrl =
    cleanText(authorProfile?.avatarUrl) || cleanText(raw.authorAvatarUrl) || "";

  const scenes = Array.isArray(raw.scenes) ? raw.scenes : [];

  const synopsis = cleanText(raw.synopsis);
  const storyTeaser = cleanText(raw.storyTeaser);
  const description = cleanText(raw.description);

  const aboutAuthor =
    cleanText(raw.aboutAuthor) ||
    cleanText(raw.about_author) ||
    cleanText(raw.about) ||
    "";

  const authorPhotoUrl =
    cleanText(raw.authorPhotoUrl) ||
    cleanText(raw.authorPhotoURL) ||
    cleanText(raw.author_photo_url) ||
    cleanText(raw.authorPhoto) ||
    "";

  const publicationCoverUrl =
    cleanText(raw.publicationCoverUrl) ||
    cleanText(raw.publishedCoverUrl) ||
    cleanText(raw.finalCoverImageUrl) ||
    "";

  const coverImageUrl =
    cleanText(raw.coverImageUrl) ||
    cleanText(raw.coverImageURL) ||
    cleanText(raw.cover_url) ||
    cleanText(raw.coverUrl) ||
    cleanText(raw.bookCoverUrl) ||
    cleanText(raw.cover) ||
    "";

  const visibilityStatus =
    cleanText(raw.visibilityStatus) ||
    cleanText(raw.readerVisibility) ||
    cleanText(raw.storyVisibility) ||
    "";

  const visibilityLabel = cleanText(raw.visibilityLabel);
  const readerVisibility = cleanText(raw.readerVisibility);
  const storyVisibility = cleanText(raw.storyVisibility);

  const publicationStatus =
    cleanText(raw.publicationStatus) ||
    cleanText(raw.publishingStatus) ||
    "";

  const publishingStatus = cleanText(raw.publishingStatus);
  const publicationTitle =
    cleanText(raw.publicationTitle) ||
    cleanText(raw.publishedTitle) ||
    "";
  const publicationSubtitle =
    cleanText(raw.publicationSubtitle) ||
    cleanText(raw.publishedSubtitle) ||
    "";

  const publicationUrl =
    cleanText(raw.publicationUrl) ||
    cleanText(raw.amazonUrl) ||
    cleanText(raw.amazonPageUrl) ||
    cleanText(raw.publishedUrl) ||
    cleanText(raw.externalPublicationUrl) ||
    "";

  const amazonUrl = cleanText(raw.amazonUrl);
  const amazonPageUrl = cleanText(raw.amazonPageUrl);
  const publishedUrl = cleanText(raw.publishedUrl);
  const externalPublicationUrl = cleanText(raw.externalPublicationUrl);

  const publishedAuthorName =
    cleanText(raw.publishedAuthorName) ||
    cleanText(raw.publicationAuthorName) ||
    cleanText(raw.displayAuthorName) ||
    "";

  const publicationAuthorName = cleanText(raw.publicationAuthorName);
  const displayAuthorName = cleanText(raw.displayAuthorName);
  const publishedAt = cleanText(raw.publishedAt);
  const publicationNote = cleanText(raw.publicationNote);

  return {
    id,
    _id: id,

    title,
    forum,

    writingMode,
    mode: writingMode,

    storyType,

    author: typeof raw.author === "string" ? cleanText(raw.author) : authorUsername,
    createdBy: cleanText(raw.createdBy) || authorUsername,

    authorId,
    authorUsername,
    authorName,
    authorAvatarUrl,
    authorProfile: {
      id: authorId,
      username: authorUsername,
      name: authorName,
      avatarUrl: authorAvatarUrl,
    },

    scenes,

    synopsis,
    storyTeaser,
    description,
    aboutAuthor,
    authorPhotoUrl,
    coverImageUrl,

    // Visibility and publication metadata are server-owned fields used by
    // Story Hub and Storylines. Preserve them during normalization; otherwise
    // refreshes fall back to old workshop/default labels.
    visibilityStatus,
    visibilityLabel,
    readerVisibility,
    storyVisibility,

    publicationStatus,
    publishingStatus,
    publicationTitle,
    publishedTitle: cleanText(raw.publishedTitle),
    publicationSubtitle,
    publishedSubtitle: cleanText(raw.publishedSubtitle),
    publicationUrl,
    amazonUrl,
    amazonPageUrl,
    publishedUrl,
    externalPublicationUrl,
    publicationCoverUrl,
    publishedCoverUrl: cleanText(raw.publishedCoverUrl),
    finalCoverImageUrl: cleanText(raw.finalCoverImageUrl),
    publishedAuthorName,
    publicationAuthorName,
    displayAuthorName,
    publishedAt,
    publicationNote,

    proposalsOpen: typeof raw.proposalsOpen === "boolean" ? raw.proposalsOpen : false,

    createdAt: raw.createdAt || null,
    updatedAt: raw.updatedAt || null,
    status: raw.status || "active",
    visibility: raw.visibility || "public",
    language: raw.language || "en",
    tags: Array.isArray(raw.tags) ? raw.tags : [],
  };
}

function mergeStoryArrays(primaryList, secondaryList) {
  const map = new Map();

  function putFrom(list) {
    if (!Array.isArray(list)) return;
    list.forEach((raw) => {
      const story = normalizeStory(raw);
      if (!story) return;
      const key = story._id || story.id;
      if (!key) return;
      map.set(String(key), story);
    });
  }

  // Secondary first, then primary wins. Used only for local storage migration.
  putFrom(secondaryList);
  putFrom(primaryList);

  return Array.from(map.values());
}

function collectAndMigrateStoriesFromStorage() {
  if (typeof window === "undefined") return [];

  const currentRaw = window.localStorage.getItem(STORAGE_KEY);
  const currentParsed = safeParseJSON(currentRaw);
  const currentStories = Array.isArray(currentParsed) ? currentParsed : [];

  let legacyStories = [];
  LEGACY_STORY_KEYS.forEach((key) => {
    const raw = window.localStorage.getItem(key);
    const parsed = safeParseJSON(raw);
    if (Array.isArray(parsed)) legacyStories = legacyStories.concat(parsed);
  });

  const merged = mergeStoryArrays(currentStories, legacyStories);

  if (canWriteLocal()) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    } catch {}
  }

  return merged;
}

export function loadStoredStories() {
  if (typeof window === "undefined") return [];
  return collectAndMigrateStoriesFromStorage();
}

export function saveStoredStories(stories) {
  if (typeof window === "undefined") return;
  if (!canWriteLocal()) return;

  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(Array.isArray(stories) ? stories : [])
    );
  } catch {}
}

/*
  Server-truth merge.

  Important rule:
  - If the server returns a valid story list, that list is authoritative.
  - Local cache may only preserve useful fields for stories that still exist on the server.
  - Local-only stories are intentionally dropped after a successful server load.
  - This prevents admin-deleted stories from reappearing in Story Hub.
*/
export function mergeStoredAndRemoteStories(storedStories, remoteStories) {
  const local = Array.isArray(storedStories) ? storedStories : [];
  const remote = Array.isArray(remoteStories) ? remoteStories : [];

  const localById = new Map();

  local.forEach((raw) => {
    const story = normalizeStory(raw);
    if (!story) return;
    const key = story._id || story.id;
    if (!key) return;
    localById.set(String(key), story);
  });

  return remote
    .map((raw) => {
      const story = normalizeStory(raw);
      if (!story) return null;

      const key = story._id || story.id;
      const localMatch = key ? localById.get(String(key)) : null;

      if (!localMatch) return story;

      return {
        ...localMatch,
        ...story,

        // Server remains authoritative for these fields.
        id: story.id,
        _id: story._id,
        scenes: story.scenes,
        status: story.status,
        visibility: story.visibility,
        visibilityStatus: story.visibilityStatus,
        visibilityLabel: story.visibilityLabel,
        readerVisibility: story.readerVisibility,
        storyVisibility: story.storyVisibility,
        publicationStatus: story.publicationStatus,
        publishingStatus: story.publishingStatus,
        publicationUrl: story.publicationUrl,
        amazonUrl: story.amazonUrl,
        amazonPageUrl: story.amazonPageUrl,
        publishedUrl: story.publishedUrl,
        externalPublicationUrl: story.externalPublicationUrl,
        publicationCoverUrl: story.publicationCoverUrl,
        publishedCoverUrl: story.publishedCoverUrl,
        finalCoverImageUrl: story.finalCoverImageUrl,
        publishedAuthorName: story.publishedAuthorName,
        publicationAuthorName: story.publicationAuthorName,
        displayAuthorName: story.displayAuthorName,
        publishedAt: story.publishedAt,
        publicationNote: story.publicationNote,
        proposalsOpen: story.proposalsOpen,
        createdAt: story.createdAt,
        updatedAt: story.updatedAt,
      };
    })
    .filter(Boolean);
}

// IMPORTANT: many parts of the app already call mergeStories(remote, stored).
// Keep that signature stable.
export function mergeStories(remote, stored) {
  return mergeStoredAndRemoteStories(stored, remote);
}